import React, { useState } from 'react';
import './RegistrationForms.css';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { trackFormSubmission } from '../utilities/trackingUtils';

const RegistrationForms = () => {
  // Parent form state
  const [parentForm, setParentForm] = useState({
    name: '',
    mobile: '',
    location: '',
    childDetails: '',
    timing: '',
    fee: '',
    mode: 'Online'
  });

  // Tutor form state
  const [tutorForm, setTutorForm] = useState({
    name: '',
    mobile: '',
    secondaryContact: '',
    location: '',
    qualifications: '',
    experience: '',
    subjects: ''
  });

  // Form submission status
  const [parentSubmitStatus, setParentSubmitStatus] = useState({ loading: false, success: false, error: null });
  const [tutorSubmitStatus, setTutorSubmitStatus] = useState({ loading: false, success: false, error: null });

  // Handle parent form changes
  const handleParentChange = (e) => {
    const { name, value } = e.target;
    setParentForm({ ...parentForm, [name]: value });
  };

  // Handle tutor form changes
  const handleTutorChange = (e) => {
    const { name, value } = e.target;
    setTutorForm({ ...tutorForm, [name]: value });
  };

  // Handle parent form submission
  const handleParentSubmit = async (e) => {
    e.preventDefault();
    setParentSubmitStatus({ loading: true, success: false, error: null });
    
    try {
      // Save to Firebase
      await addDoc(collection(db, "parents"), {
        ...parentForm,
        createdAt: serverTimestamp()
      });
      
      // Track form submission conversion
      trackFormSubmission('Parent Registration', 1.0);
      
      setParentSubmitStatus({ loading: false, success: true, error: null });
      
      // Reset form
      setParentForm({
        name: '',
        mobile: '',
        location: '',
        childDetails: '',
        timing: '',
        fee: '',
        mode: 'Online'
      });
    } catch (error) {
      console.error("Error saving parent form:", error);
      setParentSubmitStatus({ loading: false, success: false, error: error.message });
    }
  };

  // Handle tutor form submission
  const handleTutorSubmit = async (e) => {
    e.preventDefault();
    setTutorSubmitStatus({ loading: true, success: false, error: null });
    
    try {
      // Save to Firebase
      await addDoc(collection(db, "tutors"), {
        ...tutorForm,
        createdAt: serverTimestamp()
      });
      
      // Track form submission conversion
      trackFormSubmission('Tutor Registration', 1.0);
      
      setTutorSubmitStatus({ loading: false, success: true, error: null });
      
      // Reset form
      setTutorForm({
        name: '',
        mobile: '',
        secondaryContact: '',
        location: '',
        qualifications: '',
        experience: '',
        subjects: ''
      });
    } catch (error) {
      console.error("Error saving tutor form:", error);
      setTutorSubmitStatus({ loading: false, success: false, error: error.message });
    }
  };

  return (
    <section id="register" className="registration-section">
      <div className="container">
        <div className="section-header">
          <h2>Registration</h2>
          <div className="underline"></div>
        </div>
        
        <div className="registration-forms">
          {/* Parent Registration Form */}
          <div id="find-tutors" className="form-container parent-form">
            <div className="card">
              <h3>Find Tutors</h3>
              <p className="form-subtitle">Find the perfect tutor for your child</p>
              {parentSubmitStatus.success && (
                <div className="success-message">
                  Thank you for registering! We'll contact you soon.
                </div>
              )}
              {parentSubmitStatus.error && (
                <div className="error-message">
                  Error: {parentSubmitStatus.error}
                </div>
              )}
              <form onSubmit={handleParentSubmit}>
                <div className="form-group">
                  <label htmlFor="parent-name">Full Name</label>
                  <input 
                    type="text" 
                    id="parent-name" 
                    name="name" 
                    placeholder="Enter your full name" 
                    value={parentForm.name} 
                    onChange={handleParentChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="parent-mobile">Mobile Number</label>
                  <input 
                    type="tel" 
                    id="parent-mobile" 
                    name="mobile" 
                    placeholder="e.g., 1234567890" 
                    value={parentForm.mobile} 
                    onChange={handleParentChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="parent-location">Location</label>
                  <input 
                    type="text" 
                    id="parent-location" 
                    name="location" 
                    placeholder="e.g., Hyderabad" 
                    value={parentForm.location} 
                    onChange={handleParentChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="child-details">Child's Grade and Subject</label>
                  <input 
                    type="text" 
                    id="child-details" 
                    name="childDetails" 
                    placeholder="e.g., Grade 5, Math" 
                    value={parentForm.childDetails} 
                    onChange={handleParentChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="parent-timing">Preferred Timings</label>
                  <input 
                    type="text" 
                    id="parent-timing" 
                    name="timing" 
                    placeholder="e.g., 6pm to 8pm" 
                    value={parentForm.timing} 
                    onChange={handleParentChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="parent-fee">Offered Fee</label>
                  <input 
                    type="text" 
                    id="parent-fee" 
                    name="fee" 
                    placeholder="e.g., 10000/month" 
                    value={parentForm.fee} 
                    onChange={handleParentChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="parent-mode">Mode of Tuition</label>
                  <select 
                    id="parent-mode" 
                    name="mode" 
                    value={parentForm.mode} 
                    onChange={handleParentChange} 
                    required
                  >
                    <option value="Online">Online</option>
                    <option value="In-Person">In-Person</option>
                  </select>
                </div>
                
                <button 
                  type="submit" 
                  className="btn" 
                  disabled={parentSubmitStatus.loading}
                >
                  {parentSubmitStatus.loading ? 'Submitting...' : 'Register'}
                </button>
              </form>
            </div>
          </div>
          
          {/* Tutor Registration Form */}
          <div id="register-tutor" className="form-container tutor-form">
            <div className="card">
              <h3>Join as Tutor</h3>
              <p className="form-subtitle">Connect with parents seeking your expertise</p>
              {tutorSubmitStatus.success && (
                <div className="success-message">
                  Thank you for registering! We'll contact you soon.
                </div>
              )}
              {tutorSubmitStatus.error && (
                <div className="error-message">
                  Error: {tutorSubmitStatus.error}
                </div>
              )}
              <form onSubmit={handleTutorSubmit}>
                <div className="form-group">
                  <label htmlFor="tutor-name">Full Name</label>
                  <input 
                    type="text" 
                    id="tutor-name" 
                    name="name" 
                    placeholder="Enter your full name" 
                    value={tutorForm.name} 
                    onChange={handleTutorChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="tutor-mobile">Mobile Number</label>
                  <input 
                    type="tel" 
                    id="tutor-mobile" 
                    name="mobile" 
                    placeholder="e.g., 1234567890" 
                    value={tutorForm.mobile} 
                    onChange={handleTutorChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="secondary-contact">Alternate Mobile Number</label>
                  <input 
                    type="tel" 
                    id="secondary-contact" 
                    name="secondaryContact" 
                    placeholder="e.g., 0987654321" 
                    value={tutorForm.secondaryContact} 
                    onChange={handleTutorChange} 
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="tutor-location">Location</label>
                  <input 
                    type="text" 
                    id="tutor-location" 
                    name="location" 
                    placeholder="e.g., Hyderabad" 
                    value={tutorForm.location} 
                    onChange={handleTutorChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="qualifications">Qualifications</label>
                  <input 
                    type="text" 
                    id="qualifications" 
                    name="qualifications" 
                    placeholder="e.g., B.Sc. in Physics" 
                    value={tutorForm.qualifications} 
                    onChange={handleTutorChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="experience">Experience</label>
                  <input 
                    type="text" 
                    id="experience" 
                    name="experience" 
                    placeholder="e.g., 5 years teaching" 
                    value={tutorForm.experience} 
                    onChange={handleTutorChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="subjects">Preferred Subjects</label>
                  <input 
                    type="text" 
                    id="subjects" 
                    name="subjects" 
                    placeholder="e.g., Math, Physics, Chemistry" 
                    value={tutorForm.subjects} 
                    onChange={handleTutorChange} 
                    required 
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="btn"
                  disabled={tutorSubmitStatus.loading}
                >
                  {tutorSubmitStatus.loading ? 'Submitting...' : 'Register'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegistrationForms; 